export type flavoursType = {
	[key: string]: {
		name: string;
		notes: string[];
		allergens: string[];
		price?: number;
		baseFlavours: string[];
	};
};

const alcoholic =
	'This product contains alcohol. The alcohol may be substituted for a non-alcoholic version upon request.';
const ambient =
	'This is an ambient product and does not require refrigeration. It is best enjoyed at room temperature.';
const chilled =
	'This is a chilled product and requires refrigeration. It is best enjoyed cold and should be kept refrigerated at all times.';
const chilledCreamCheeseFrosting =
	'Due to the scrumptious award-winning cream cheese frosting, this is a chilled product and requires refrigeration. It is best enjoyed cold and should be kept refrigerated at all times.';
const chilledFreshStrawberries =
	'Due to the fresh strawberries, this is a chilled product and requires refrigeration. It is best enjoyed cold and should be kept refrigerated at all times.';
const chilledFreshWhippedCream =
	'Due to the fresh whipped cream, this is a chilled product and requires refrigeration. It is best enjoyed cold and should be kept refrigerated at all times.';
const chilledHeatBeforeUse =
	'This is a chilled product and requires refrigeration. It is best enjoyed cold and should be kept refrigerated at all times. For best results, heat before use.';

/*
 Al of the regular flavours are defined here. The notes and allergens are defined for each flavour.
These flavours do not have an extra cost when selected
 */

export const regularFlavours: flavoursType = {
	bf: {
		name: 'Black Forest',
		notes: [chilledFreshWhippedCream, alcoholic],
		allergens: ['Alcohol', 'Cherry'],
		baseFlavours: ['Black Forest']
	},
	ca: {
		name: 'Carrot',
		notes: [chilledCreamCheeseFrosting],
		allergens: ['Walnuts'],
		baseFlavours: ['Carrot', 'Cream Cheese']
	},
	ccSa: {
		name: 'Cream Cheese',
		notes: [chilledHeatBeforeUse],
		allergens: [],
		baseFlavours: ['Cream Cheese']
	},
	ch: {
		name: 'Chocolate',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Chocolate']
	},
	chVaAVaVa: {
		name: 'Chocolate and Vanilla with Vanilla Frosting',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Chocolate', 'Vanilla']
	},
	chCc: {
		name: 'Chocolate with Cream Cheese Frosting',
		notes: [chilledCreamCheeseFrosting],
		allergens: [],
		baseFlavours: ['Chocolate', 'Cream Cheese']
	},
	chCh: {
		name: 'Chocolate with Chocolate Frosting',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Chocolate']
	},
	chVa: {
		name: 'Chocolate with Vanilla Frosting',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Chocolate', 'Vanilla']
	},
	chChSt: {
		name: 'Chocolate with Chocolate Frosting and Fresh Strawberries',
		notes: [chilledFreshStrawberries],
		allergens: [],
		baseFlavours: ['Chocolate']
	},
	ci: {
		name: 'Cinnamon',
		notes: [chilledCreamCheeseFrosting],
		allergens: [],
		baseFlavours: ['Cinnamon', 'Cream Cheese']
	},
	kl: {
		name: 'Key Lime',
		notes: [chilledFreshWhippedCream],
		allergens: [],
		baseFlavours: ['Key Lime']
	},
	or: {
		name: 'Oreo',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Oreo', 'Chocolate']
	},
	pb: {
		name: 'Peanut Butter',
		notes: [chilled],
		allergens: ['Peanuts'],
		baseFlavours: ['Peanut Butter']
	},
	pe: {
		name: 'Pecan',
		notes: [ambient, alcoholic],
		allergens: ['Alcohol', 'Pecans'],
		baseFlavours: ['Pecan']
	},
	pePu: {
		name: 'Pecan and Pumpkin',
		notes: [alcoholic, chilled],
		allergens: ['Alcohol', 'Pecans'],
		baseFlavours: ['Pecan', 'Pumpkin']
	},
	pu: {
		name: 'Pumpkin',
		notes: [chilled],
		allergens: [],
		baseFlavours: ['Pumpkin']
	},
	puCc: {
		name: 'Pumpkin with Cream Cheese Frosting',
		notes: [chilledCreamCheeseFrosting],
		allergens: [],
		baseFlavours: ['Pumpkin', 'Cream Cheese']
	},
	rv: {
		name: 'Red Velvet',
		notes: [chilledCreamCheeseFrosting],
		allergens: [],
		baseFlavours: ['Red Velvet', 'Cream Cheese']
	},
	sc: {
		name: 'Salted Caramel',
		notes: [ambient, alcoholic],
		allergens: ['Alcohol'],
		baseFlavours: ['Salted Caramel']
	},
	scSa: {
		name: 'Salted Caramel',
		notes: [chilledHeatBeforeUse, alcoholic],
		allergens: ['Alcohol'],
		baseFlavours: ['Salted Caramel']
	},
	vaCh: {
		name: 'Vanilla with Chocolate Frosting',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Vanilla', 'Chocolate']
	},
	vaVa: {
		name: 'Vanilla with Vanilla Frosting',
		notes: [ambient],
		allergens: [],
		baseFlavours: ['Vanilla']
	}
};

/*
 All of the premium flavours are defined here. The notes and allergens are defined for each flavour.
These flavours have an extra cost when selected
 */
export const premiumFlavours: flavoursType = {
	orCupcakes: {
		name: 'Oreo',
		notes: [ambient],
		allergens: [],
		price: 2,
		baseFlavours: ['Oreo', 'Chocolate']
	}
};

// combines the flavours into an object
const combinedFlavours: flavoursType = {
	...regularFlavours,
	...premiumFlavours
};

// create a new object with alphabetically sorted keys
export const flavours: flavoursType = Object.keys(combinedFlavours)
	.sort((a, b) => a.localeCompare(b))
	.reduce((sortedFlavours: flavoursType, key: string) => {
		sortedFlavours[key] = combinedFlavours[key];
		return sortedFlavours;
	}, {});

const getUniqueBaseFlavours = () => {
	const baseFlavours = Object.values(flavours).map(
		(flavour) => flavour.baseFlavours
	);
	return [...new Set(baseFlavours.flat())].sort((a, b) => a.localeCompare(b));
};

export const baseFlavours = getUniqueBaseFlavours();
